import {
  TRACK_IDS,
  EDUCATION_JOYRIDE_TARGETS,
} from '@edulastic/constants/educationJoyrides'
import {
  buildEducationJoyrideStep,
  buildJoyrideBulletBody,
  buildModerateEducationJoyrideStep,
} from '../constants'
import { getModerateTrackContent } from '../moderateTrackContent'
import searchPreviewImg from '../../../../TestList/assets/education-joyride/search-district-library-preview.png'
import lcbPreviewImg from '../../GroupAndAssign/assets/education-joyride/recommendations-lcb-preview.png'
import classProfilePreviewImg from '../../../../Reports/subPages/Profiles/assets/education-joyride/recommendations-class-profile-preview.png'
import studentProfilePreviewImg from '../../../../Reports/subPages/Profiles/assets/education-joyride/recommendations-student-profile-preview.png'
import genaiPreviewImg from '../../../../Dashboard/assets/education-joyride/genai-ilp-preview.png'
import curriculumPreviewImg from '../../../../Curriculum/assets/education-joyride/curriculum-pa-preview.png'
import testMetadataAssistancePreviewImg from '../../../../AIFeatures/assets/education-joyride/test-metadata-assistance-preview.png'

const buildModerateStep = (
  trackId,
  { target, titleId, descId, placement, floaterProps }
) => {
  const { title, body } = getModerateTrackContent(trackId)

  return buildModerateEducationJoyrideStep({
    target,
    title,
    body,
    placement,
    floaterProps,
    titleId,
    descId,
  })
}

const TRACK_CONFIGS = {
  [TRACK_IDS.SEARCH_DISTRICT_LIBRARY]: {
    target: EDUCATION_JOYRIDE_TARGETS.DISTRICT_LIBRARY_FILTER,
    buildStep: ({ target, variant, titleId, descId }) => {
      if (variant === 'moderate') {
        return buildModerateStep(TRACK_IDS.SEARCH_DISTRICT_LIBRARY, {
          target,
          titleId,
          descId,
          placement: 'right',
        })
      }

      return buildEducationJoyrideStep({
        target,
        previewImg: searchPreviewImg,
        previewAlt: 'District Library filter preview',
        title: 'Introducing District Library Filter',
        body: `This helps easily access relevant content shared district-wide. Additionally, a Certified Content subfolder appears when content from purchased/district collections is available.`,
        placement: 'right',
        titleId,
        descId,
      })
    },
  },
  [TRACK_IDS.RECOMMENDATIONS_LCB]: {
    target: EDUCATION_JOYRIDE_TARGETS.IDENTIFY_LEARNING_GAPS,
    buildStep: ({ target, variant, titleId, descId }) => {
      if (variant === 'moderate') {
        return buildModerateStep(TRACK_IDS.RECOMMENDATIONS_LCB, {
          target,
          titleId,
          descId,
          placement: 'left',
        })
      }

      return buildEducationJoyrideStep({
        target,
        previewImg: lcbPreviewImg,
        previewAlt: 'Identify Learning Gaps button preview',
        title: 'Identify Learning Gaps',
        body:
          'Use Pear AI to group students and get personalized follow-up recommendations.',
        placement: 'left',
        titleId,
        descId,
      })
    },
  },
  [TRACK_IDS.RECOMMENDATIONS_CLASS_PROFILE]: {
    target: EDUCATION_JOYRIDE_TARGETS.PLAN_INTERVENTION,
    buildStep: ({ target, variant, titleId, descId }) => {
      if (variant === 'moderate') {
        return buildModerateStep(TRACK_IDS.RECOMMENDATIONS_CLASS_PROFILE, {
          target,
          titleId,
          descId,
          placement: 'left',
        })
      }

      return buildEducationJoyrideStep({
        target,
        previewImg: classProfilePreviewImg,
        previewAlt: 'Plan intervention button preview',
        title: 'Plan Targeted Interventions',
        body: buildJoyrideBulletBody(
          'Close learning gaps across your class. Based on mastery data, we:',
          [
            'Group students by performance bands for the selected standard.',
            "Recommend tailored resources to support each group's level. You may Generate AI materials on the spot for targeted reteaching.",
          ]
        ),
        placement: 'left',
        titleId,
        descId,
      })
    },
  },
  [TRACK_IDS.RECOMMENDATIONS_STUDENT_PROFILE]: {
    target: EDUCATION_JOYRIDE_TARGETS.PLAN_INTERVENTION,
    buildStep: ({ target, variant, titleId, descId }) => {
      if (variant === 'moderate') {
        return buildModerateStep(TRACK_IDS.RECOMMENDATIONS_STUDENT_PROFILE, {
          target,
          titleId,
          descId,
          placement: 'left',
        })
      }

      return buildEducationJoyrideStep({
        target,
        previewImg: studentProfilePreviewImg,
        previewAlt: 'Plan intervention button preview',
        title: 'Plan Targeted Interventions',
        body: buildJoyrideBulletBody(
          "Close learning gaps with personalized support. Based on this student's mastery data, we:",
          [
            'Recommend tailored resources aligned to improve mastery of the standard.',
            'You may Generate custom AI materials for immediate 1-on-1 practice.',
          ]
        ),
        placement: 'left',
        titleId,
        descId,
      })
    },
  },
  [TRACK_IDS.GENAI_DASHBOARD_ILP]: {
    target: EDUCATION_JOYRIDE_TARGETS.DASHBOARD_ILP,
    buildStep: ({ target, variant, titleId, descId }) => {
      if (variant === 'moderate') {
        return buildModerateStep(TRACK_IDS.GENAI_DASHBOARD_ILP, {
          target,
          titleId,
          descId,
          placement: 'bottom',
        })
      }

      return buildEducationJoyrideStep({
        target,
        previewImg: genaiPreviewImg,
        previewAlt: 'Instant Lesson Packages button preview',
        title: 'Generate with AI',
        body: `Quickly generate a test or lesson deck with a built-in review flow. Just describe what you need, preview and edit the AI's output, and import it when you're ready.`,
        placement: 'bottom',
        titleId,
        descId,
      })
    },
  },
  [TRACK_IDS.CURRICULUM_PA]: {
    target: EDUCATION_JOYRIDE_TARGETS.CURRICULUM_PAGE,
    spotlightUsesOuterTarget: true,
    buildStep: ({ target, variant, titleId, descId }) => {
      const floaterProps = {
        offset: 12,
        options: {
          flip: { enabled: false },
          preventOverflow: {
            boundariesElement: 'viewport',
            padding: 16,
          },
        },
      }

      if (variant === 'moderate') {
        return buildModerateStep(TRACK_IDS.CURRICULUM_PA, {
          target,
          titleId,
          descId,
          placement: 'right',
          floaterProps,
        })
      }

      return buildEducationJoyrideStep({
        target,
        previewImg: curriculumPreviewImg,
        previewAlt: 'Curriculum page preview',
        title: 'Introducing Curriculum Hub',
        body: buildJoyrideBulletBody(
          'Connect district pacing with daily instruction.',
          [
            'Admins: Upload and publish official guides to keep schools aligned, and link aligned resources directly to modules.',
            'Teachers: Browse official pacing and sequencing to guide weekly lesson planning.',
          ]
        ),
        placement: 'right',
        floaterProps,
        titleId,
        descId,
      })
    },
  },
  [TRACK_IDS.TEST_METADATA_ASSISTANCE]: {
    target: EDUCATION_JOYRIDE_TARGETS.TEST_METADATA_BANNER,
    scrollIntoView: false,
    spotlightUsesOuterTarget: true,
    buildStep: ({ target, variant, titleId, descId }) => {
      const floaterProps = {
        offset: 24,
        options: {
          flip: { enabled: false },
          preventOverflow: {
            enabled: false,
          },
        },
      }

      if (variant === 'moderate') {
        return buildModerateStep(TRACK_IDS.TEST_METADATA_ASSISTANCE, {
          target,
          titleId,
          descId,
          placement: 'left-start',
          floaterProps,
        })
      }

      return buildEducationJoyrideStep({
        target,
        previewImg: testMetadataAssistancePreviewImg,
        previewAlt: 'Pear Assist metadata suggestion preview',
        title: 'Introducing Pear Assist',
        body:
          'Review and attach Standards, DoK, Difficulty and Rubrics to all your items.',
        placement: 'left-start',
        floaterProps,
        titleId,
        descId,
      })
    },
  },
  [TRACK_IDS.FOLDER_SHARING]: {
    target: EDUCATION_JOYRIDE_TARGETS.FOLDER_SHARING,
    scrollIntoView: false,
    spotlightUsesOuterTarget: true,
    buildStep: ({ target, variant, titleId, descId }) => {
      const floaterProps = {
        offset: 24,
        options: {
          flip: { enabled: false },
          preventOverflow: {
            enabled: false,
          },
        },
      }

      if (variant === 'moderate') {
        return buildModerateStep(TRACK_IDS.FOLDER_SHARING, {
          target,
          titleId,
          descId,
          placement: 'right',
          floaterProps,
        })
      }

      return buildEducationJoyrideStep({
        target,
        title: 'Share Test Folders',
        body:
          'Share folders with colleagues as Viewer or Editor. Folder sharing does not change test permissions.',
        placement: 'right',
        floaterProps,
        titleId,
        descId,
      })
    },
  },
  [TRACK_IDS.ASSIGNMENT_FOLDER_SHARING]: {
    target: EDUCATION_JOYRIDE_TARGETS.ASSIGNMENT_FOLDER_SHARING,
    scrollIntoView: false,
    spotlightUsesOuterTarget: true,
    buildStep: ({ target, variant, titleId, descId }) => {
      const floaterProps = {
        offset: 24,
        options: {
          flip: { enabled: false },
          preventOverflow: {
            enabled: false,
          },
        },
      }

      if (variant === 'moderate') {
        return buildModerateStep(TRACK_IDS.ASSIGNMENT_FOLDER_SHARING, {
          target,
          titleId,
          descId,
          placement: 'right',
          floaterProps,
        })
      }

      return buildEducationJoyrideStep({
        target,
        title: 'Share Assignment Folders',
        body:
          'Share folders with district users as Viewer or Editor. Folder sharing applies only to the folder and does not change permissions on the assignments inside.',
        placement: 'right',
        floaterProps,
        titleId,
        descId,
      })
    },
  },
}

export const getTrackConfig = (trackId) => TRACK_CONFIGS[trackId] ?? null

export default TRACK_CONFIGS
